import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, Share2, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { toast } from 'sonner';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1672736810868-130c00ef5a9e?w=600&q=80';

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { isInWishlist, toggleItem } = useWishlist();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock > 0) {
      addItem(product, 1);
      toast.success(`${product.name} added to cart!`);
    }
  };

  const handleToggleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = toggleItem(product);
    if (added) {
      toast.success(`${product.name} added to wishlist!`);
    } else {
      toast.success(`${product.name} removed from wishlist`);
    }
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/product/${product.id}`;
    const shareText = `Check out ${product.name} from Reggae Essences!`;
    
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: shareText,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="product-card group relative overflow-hidden rounded-3xl bg-cream border border-warm-brown/10"
    >
      {/* Image */}
      <div className="aspect-[3/4] overflow-hidden bg-sand">
        <img
          src={product.images[0] || PLACEHOLDER_IMG}
          alt={product.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
        />
      </div>

      {/* Stock Badge */}
      {product.stock <= 5 && product.stock > 0 && (
        <span className="absolute top-4 left-4 bg-terracotta text-cream text-xs font-bold px-3 py-1 rounded-full">
          Only {product.stock} left!
        </span>
      )}
      {product.stock === 0 && (
        <span className="absolute top-4 left-4 bg-warm-brown text-cream text-xs font-bold px-3 py-1 rounded-full">
          Out of Stock
        </span>
      )}

      {/* Quick Actions */}
      <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleToggleWishlist}
          data-testid={`wishlist-btn-${product.id}`}
          className={`p-2 rounded-full shadow-lg transition-colors ${
            isInWishlist(product.id) 
              ? 'bg-red-500 text-white' 
              : 'bg-cream hover:bg-sand'
          }`}
        >
          <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? 'fill-white' : 'text-forest'}`} />
        </button>
        <button
          onClick={handleShare}
          data-testid={`share-btn-${product.id}`}
          className="p-2 bg-cream rounded-full shadow-lg hover:bg-sand transition-colors"
        >
          <Share2 className="h-4 w-4 text-forest" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <span className="text-xs font-mono uppercase tracking-wider text-warm-brown">
          {formatCategory(product.category)}
        </span>
        <h3 className="font-heading font-semibold text-lg text-forest mt-1 mb-2 line-clamp-1">
          {product.name}
        </h3>
        <p className="text-warm-brown text-sm line-clamp-2 mb-4">
          {product.description}
        </p>

        <div className="flex items-center justify-between">
          <span className="price-tag text-xl font-bold text-forest">
            ${product.price.toFixed(2)}
          </span>
          <button
            onClick={handleAddToCart}
            disabled={product.stock === 0}
            data-testid={`add-to-cart-${product.id}`}
            className={`p-3 rounded-full transition-all ${
              product.stock > 0
                ? 'bg-forest text-cream hover:bg-forest-light'
                : 'bg-warm-brown/30 text-warm-brown cursor-not-allowed'
            }`}
          >
            <ShoppingCart className="h-5 w-5" />
          </button>
        </div>
      </div>
    </Link>
  );
};

export const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating ? 'fill-gold text-gold' : 'text-warm-brown/30'
          }`}
        />
      ))}
    </div>
  );
};
